import {
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExposureTypeClientEnum,
  MixpanelEvent,
  baseTreeGroupColumnDef,
  filterExistsAndExcludes,
  removeEmptyFilters,
  stringColumnComparatorMultiColumn,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterTable,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useWSFilterPipe,
  type CreditBlotterExposure,
  type FilterClause,
  type FilterableProperty,
  type Leaves,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import type { GridOptions, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { map, pipe } from 'rxjs';
import { defaultCreditFilter } from './defaults';
import type { CreditTabProps, CreditTableFilter } from './types';
import { useCreditBlotterDataObs } from './useCreditBlotterDataObs';
import { useCreditColumns } from './useCreditColumns';
import { useCreditFilter } from './useCreditFilter';
import { useCreditMenu } from './useCreditMenu';

const creditBlotterExposureComparator = stringColumnComparatorMultiColumn<CreditBlotterExposure>([
  'MarketAccount',
  'Currency',
  'ExposureDefinition',
]);

function getCreditBlotterExposureKey(cbe: CreditBlotterExposure): string {
  return cbe.rowID;
}

function getCreditBlotterDataPath(item: CreditBlotterExposure) {
  return item.treePath;
}

const CREDIT_AGG_SPECS = [
  {
    valuePath: 'effectiveExposureWithSign',
    currencyPath: 'ExposureCurrency',
  },
  {
    valuePath: 'effectiveRemainingCredit',
    currencyPath: 'ExposureCurrency',
  },
  {
    valuePath: 'effectiveLimitWithSign',
    currencyPath: 'ExposureCurrency',
  },
  {
    valuePath: 'effectiveSide.talosExposure',
    currencyPath: 'ExposureCurrency',
  },
  {
    valuePath: 'effectiveSide.talosExposureLimit',
    currencyPath: 'ExposureCurrency',
  },
] satisfies {
  valuePath: Leaves<CreditBlotterExposure>;
  currencyPath: Leaves<CreditBlotterExposure>;
}[];

interface UseCreditBlotterFoundationParams {
  blotterID: string;
  tab: CreditTabProps;
  onUpdateTab: (tab: CreditTabProps) => void;
  initialIsOpen?: boolean;
  enableContextMenus?: boolean;
  enableCreditMenu?: boolean;
  enableRowSelection?: boolean;
  enableTotals?: boolean;
}

export function useCreditBlotterFoundation({
  blotterID,
  tab,
  onUpdateTab,
  initialIsOpen,
  enableContextMenus = false,
  enableCreditMenu = false,
  enableRowSelection = false,
  enableTotals = true,
}: UseCreditBlotterFoundationParams) {
  const mixpanel = useMixpanel();

  const defaultColumns = useCreditColumns(tab.defaultColumns);
  const defaultFilter = tab.defaultFilter ?? defaultCreditFilter;
  const showZeroBalances = tab.showZeroBalances ?? true;
  const showTotals = tab.showTotals ?? false;

  const { dataObservable, errorObservable } = useCreditBlotterDataObs({
    showZeroBalances,
    tag: 'Credit blotter',
  });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filterResults = useCreditFilter({
    persistedBlotterTable,
  });

  const { changeFilter, filterableProperties, initialFilterClauses, filter } = filterResults;
  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<CreditTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) as unknown as CreditTableFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );
  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const creditMenu = useCreditMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties,
  });

  const columns = useMemo(
    () => (enableCreditMenu ? [...defaultColumns, creditMenu.column] : defaultColumns),
    [creditMenu.column, defaultColumns, enableCreditMenu]
  );

  const rxjsFilterFunc = useCallback(
    (data: CreditBlotterExposure) => {
      if (filterExistsAndExcludes(filter, 'Accounts', data, 'MarketAccount')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Currencies', data, 'Currency')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Types', data, 'type')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Statuses', data, 'status')) {
        return false;
      }
      return true;
    },
    [filter]
  );
  const filterPipe = useWSFilterPipe({ getUniqueKey: getCreditBlotterExposureKey, filterFunc: rxjsFilterFunc });
  const filteredDataObservable = useMemo(() => dataObservable.pipe(filterPipe), [filterPipe, dataObservable]);

  const autoGroupColumnDef = useMemo<GridOptions<CreditBlotterExposure>['autoGroupColumnDef']>(
    () =>
      ({
        ...baseTreeGroupColumnDef,
        valueFormatter: ({ data, context, node }: ValueFormatterParams<CreditBlotterExposure>) => {
          const marketAccountName = data?.MarketAccount ?? node?.key;
          if (!marketAccountName) {
            return 'Unknown';
          }

          const marketAccount = context.current.marketAccountsByName?.get(marketAccountName);
          return marketAccount?.DisplayName ?? marketAccount?.Name ?? '';
        },
        filterValueGetter: ({ data }: ValueGetterParams<CreditBlotterExposure>) => {
          return data?.MarketAccount;
        },
        headerName: 'Market Account',
        initialSort: 'asc',
        comparator: (valueA, valueB, nodeA, nodeB) => {
          if (nodeA.data == null || nodeB.data == null) {
            return 0;
          }
          return creditBlotterExposureComparator(nodeA.data, nodeB.data);
        },
      }) satisfies GridOptions<CreditBlotterExposure>['autoGroupColumnDef'],
    []
  );

  const aggDeltaUpdatesPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getCreditBlotterExposureKey,
    aggSpecs: CREDIT_AGG_SPECS,
  });
  const totalsPipe = useMemo(() => {
    return pipe(
      map((json: MinimalSubscriptionResponse<CreditBlotterExposure>) => {
        return { ...json, data: json.data.filter(cbe => cbe.type === ExposureTypeClientEnum.Exposure) };
      }),
      aggDeltaUpdatesPipe
    );
  }, [aggDeltaUpdatesPipe]);

  const blotterTable = useBlotterTable<CreditBlotterExposure>({
    dataObservable: filteredDataObservable,
    errorObservable,
    pinnedRowDataPipe: enableTotals ? totalsPipe : undefined,
    rowID: 'rowID' satisfies keyof CreditBlotterExposure,
    persistence: persistedBlotterTable,
    showPinnedRows: enableTotals && showTotals,
    columns,
    contextMenuOptions: enableContextMenus
      ? {
          getCustomContextMenuItems: creditMenu.getCustomContextMenuItems,
        }
      : undefined,
    gridOptions: {
      showOpenedGroup: true,
      autoGroupColumnDef,
      treeData: true,
      getDataPath: getCreditBlotterDataPath,
      ...(enableRowSelection ? { rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS } : undefined),
    },
    exportOptions: {
      baseParams: { skipRowGroups: false },
      name: 'Credit',
      tabLabel: tab.label,
    },
  });

  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const updateShowTotals = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowTotals);
    onUpdateTab({ ...tab, showTotals: value });
  });

  return {
    blotterTable,
    creditMenuDialogs: enableCreditMenu ? creditMenu.dialogs : null,
    filterBuilderAccordion,
    showTotals,
    showZeroBalances,
    updateShowTotals,
    updateShowZeroBalances,
  };
}
